import React, { useRef, useState } from "react";
import "./ReminderSubscription.scss";
import utilService from "../../services/UtilService";
import subscriptionService from "../../services/ReminderSubscriptionService";
import { Channel, SubscribeRateAgreementData } from "../../types/SubscribeRateAgreementData";
import { SPOTButton } from "spot-component-library/lib/components/SPOTButton";
import rateAgreementService from "../../services/RateAgreementService";
import { SPOTNotification } from "spot-component-library/lib/components/notification/SPOTNotification";
import { useForm } from "react-hook-form";
import { SPOTInput } from "spot-component-library/lib/components/form/spotInput/SPOTInput";
import classNames from "classnames";
import { useTranslation } from "react-i18next";
import { SubscriptionVerificationData } from "../../types/SubscriptionVerificationData";
import { Dialog } from "spot-component-library/lib/components/basic/dialog/Dialog";
import { readLanguageFromCookie } from "../../utils/helper";

type ActivePAge = "subscriptionPage" | "verificationPage" | "successPage";
type ErrorTypes =
    | ""
    | "debt-number-error"
    | "channel-not-selected"
    | "subscription-error"
    | "request-error"
    | "reminder-contact-info"
    | "language-error"
    | "contact-verification-error";

export const ReminderSubscription = () => {
    const [subscribeData, setSubscribeData] = useState<SubscribeRateAgreementData>({} as SubscribeRateAgreementData);
    const [verifyData, setVerifyData] = useState<SubscriptionVerificationData>({} as SubscriptionVerificationData);
    const [isError, setIsError] = useState<ErrorTypes>("");
    const [activePage, setActivePage] = useState<ActivePAge>("subscriptionPage");
    const [reminderRegexPattern, setReminderRegexPattern] = useState<RegExp>(/[]/);
    const [reminderRegexPatternText, setReminderRegexPatternText] = useState<string>("");
    const [disableReminder, setDisableReminder] = useState<boolean>(false);
    const [, setDebtNo] = useState<string>("");

    const disableReminderRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();
    const channelNotSelectedError = "channel-not-selected";

    const {
        register,
        setError,
        setValue,
        formState: { errors, isValid },
        trigger,
    } = useForm({ mode: "onBlur" });

    async function checkRateAgreementPossible() {
        const debtNumber = await utilService
            .getDebtNo()
            .then((debtNoResponse) => {
                if (!debtNoResponse || debtNoResponse === " ") {
                    setDisableReminder(true);
                    reminderServiceErrorHandler("debt-number-error");
                    setError("debt-number-error", { type: "focus", message: t("REMINDER.ERRORS.DEBT_NUMBER_ERROR") });
                    return;
                }

                const subscribeDataRef = subscribeData;
                subscribeDataRef.debtNumber = debtNoResponse;
                setSubscribeData(subscribeDataRef);

                const verifyDataRef = verifyData;
                verifyDataRef.debtNumber = debtNoResponse;
                setVerifyData(verifyDataRef);

                setDebtNo(debtNoResponse);
                return debtNoResponse;
            })
            .catch(() => {
                setDisableReminder(true);
                reminderServiceErrorHandler("debt-number-error");
                setError("request-error", { type: "custom", message: t("REMINDER.ERRORS.REQUEST_ERROR") });
            });

        if (debtNumber) {
            await rateAgreementService
                .getActiveRateAgreement(debtNumber)
                .then((response) => {
                    if (!response || !response.rateAgreementId) {
                        return;
                    }

                    const subscribeDataRef = subscribeData;
                    subscribeDataRef.spotRateAgreementId = response.rateAgreementId;
                    setSubscribeData(subscribeDataRef);

                    const verifyDataRef = verifyData;
                    verifyDataRef.spotRateAgreementId = response.rateAgreementId;
                    setVerifyData(verifyDataRef);
                })
                .catch(() => {
                    setError("request-error", { type: "custom", message: t("REMINDER.ERRORS.REQUEST_ERROR") });
                });
        }
    }

    React.useEffect(() => {
        checkRateAgreementPossible().then();

        if (disableReminder && disableReminderRef && disableReminderRef.current) {
            disableReminderRef.current.style.pointerEvents = "none";
        }
    }, [activePage, disableReminder]);

    const selectReminderChannel = (channel: Channel) => {
        const subData = { ...subscribeData };
        subData.channel = channel;
        subData.emailAddress = "";
        subData.phoneNumber = 0;

        if (channel == Channel.SMS) {
            setReminderRegexPatternText(t("APP_ROOT.VALIDATION.TELEPHONE_INVALID"));
            setReminderRegexPattern(/^\+[1-9]\d{5,21}$/);
        }

        if (channel == Channel.EMAIL) {
            setReminderRegexPatternText(t("APP_ROOT.VALIDATION.EMAIL_INVALID"));
            setReminderRegexPattern(
                /^[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-zA-Z0-9!#$%&'*+/=?^_`{|}~-]+)*@(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?\.)+[a-zA-Z0-9](?:[a-zA-Z0-9-]*[a-zA-Z0-9])?$/
            );
        }

        setValue("reminder-contact-info", "");
        setError(`${isError}`, {});
        reminderServiceErrorHandler("");

        setSubscribeData(subData);
    };

    const setReminderChannelHandler = (evt: any) => {
        const subData = { ...subscribeData };
        const value = evt.target.value;

        if (subData.channel === Channel.EMAIL) {
            subData.emailAddress = value;
        }

        if (subData.channel === Channel.SMS) {
            if (isNaN(value) && value !== "+") {
                setValue("reminder-contact-info", "");
                trigger().then();
                return;
            }
            subData.phoneNumber = Number(value);
        }
        setSubscribeData(subData);
    };

    const subscribe = async () => {
        const language = readLanguageFromCookie();

        const subscribeDataRef = subscribeData;
        subscribeDataRef.language = language;
        setSubscribeData(subscribeDataRef);

        await subscriptionService
            .subscribe(subscribeData)
            .then((response) => {
                if (!response) {
                    setError("subscription-error", { type: "custom", message: t("REMINDER.ERRORS.SUBSCRIPTION_ERROR") });
                    reminderServiceErrorHandler("subscription-error");
                    setError("subscription-error", { type: "custom", message: t("REMINDER.ERRORS.SUBSCRIPTION_ERROR") });
                    setActivePage("subscriptionPage");
                    return;
                }
                setActivePage("verificationPage");
            })
            .catch(() => {
                setError("subscription-error", { type: "custom", message: t("REMINDER.ERRORS.SUBSCRIPTION_ERROR") });
                reminderServiceErrorHandler("subscription-error");
            });
    };

    const isContactChannelVerified = async () => {
        const response = await subscriptionService.verifyCode(verifyData).catch(() => {
            setError("contact-verification-error", { type: "custom", message: t("REMINDER.ERRORS.CONTACT_VERIFICATION_ERROR") });
            reminderServiceErrorHandler("contact-verification-error");
        });

        if (!response) {
            reminderServiceErrorHandler("contact-verification-error");
            setError("contact-verification-error", { type: "focus", message: t("REMINDER.ERRORS.INVALID_VERIFICATION_CODE") }, { shouldFocus: true });
            return;
        }
        setActivePage("successPage");
    };

    const reminderServiceErrorHandler = (errorName: ErrorTypes) => {
        setIsError(errorName);
    };

    const onChangeHandler = (evt: any) => {
        const target = evt.target;
        const verificationCode = target.value;
        setVerifyData({ ...verifyData, verificationCode });
    };

    const isReminderButtonActive = (): boolean => {
        return subscribeData.channel !== undefined && isValid;
    };

    return (
        <div id={"rate-agreement-reminder-page"}>
            {activePage === "subscriptionPage" && (
                <div className={"subscribe-reminder-wrapper"} ref={disableReminderRef}>
                    <p>
                        <strong>{t("REMINDER.TITLE")}</strong>
                        {t("REMINDER.HEADLINE")}
                    </p>
                    <div className={"reminder-channels-container"}>
                        <div className={"reminder-channels-panel"}>
                            <div
                                id={"email-channel"}
                                className={classNames("reminder-channels", {
                                    "reminder-channels-error": isError === `${channelNotSelectedError}`,
                                    "selected-channel-background-color icon-checkmark": subscribeData.channel == Channel.EMAIL,
                                })}
                                onClick={() => selectReminderChannel(Channel.EMAIL)}>
                                <div className={"reminder-channels-label-icon-wrapper"}>
                                    <div data-test={"email-channel-label"}>
                                        <i className={"icon-e-mail-V2"}></i>
                                        <label htmlFor={"EMAIL"}>{t("REMINDER.CHANNEL_LABEL.EMAIL")}</label>
                                    </div>
                                </div>
                            </div>

                            <div
                                id={"sms-channel"}
                                className={classNames("reminder-channels", {
                                    "reminder-channels-error": isError === `${channelNotSelectedError}`,
                                    "selected-channel-background-color icon-checkmark": subscribeData.channel == Channel.SMS,
                                })}
                                onClick={() => selectReminderChannel(Channel.SMS)}>
                                <div className={"reminder-channels-label-icon-wrapper"}>
                                    <div>
                                        <i className={"icon-smartphone"}></i>
                                        <label htmlFor={"SMS"}>{t("REMINDER.CHANNEL_LABEL.SMS")}</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {subscribeData && subscribeData.channel && (
                        <div className={"reminder-spot-input-wrapper"} data-test="reminder-contact-info">
                            <SPOTInput
                                label={subscribeData.channel === Channel.EMAIL ? t("REMINDER.PLACEHOLDER_EMAIL") : t("REMINDER.PLACEHOLDER_SMS")}
                                id={"reminder-contact-info"}
                                name={"reminder-contact-info"}
                                onChange={setReminderChannelHandler}
                                register={register}
                                placeholder={subscribeData.channel === Channel.EMAIL ? t("REMINDER.PLACEHOLDER_EMAIL") : t("REMINDER.PLACEHOLDER_SMS")}
                                required={subscribeData.channel === Channel.SMS ? t("APP_ROOT.VALIDATION.TELEPHONE_INVALID") : t("APP_ROOT.VALIDATION.EMAIL_INVALID")}
                                defaultValue={subscribeData.emailAddress || String(subscribeData.phoneNumber)}
                                errors={errors}
                                maxLength={50}
                                minLength={6}
                                pattern={{
                                    value: reminderRegexPattern,
                                    message: reminderRegexPatternText,
                                }}
                            />
                        </div>
                    )}

                    {subscribeData && subscribeData.channel && isValid && (
                        <div className={"reminder-spot-btn-wrapper"}>
                            <SPOTButton
                                id={"reminder-subscribe-btn"}
                                disabled={!isReminderButtonActive()}
                                type={"primary"}
                                label={t("REMINDER.SUBSCRIBE_BTN")}
                                onClick={subscribe}
                            />
                        </div>
                    )}

                    {isError && errors && errors[`${isError}`] && isError === `${channelNotSelectedError}` && (
                        <object id={`${channelNotSelectedError}`} name={`${channelNotSelectedError}`}>
                            {<SPOTNotification text={String(errors[`${isError}`]?.message)} active={true} type={"error"} />}
                        </object>
                    )}
                </div>
            )}

            <div className={"reminder-errors"}>
                {isError &&
                    errors &&
                    channelNotSelectedError !== isError &&
                    isError !== "reminder-contact-info" &&
                    (isError === "language-error" || isError === "request-error" || isError === "subscription-error") && (
                        <object id={`${isError}`} name={`${isError}`}>
                            {errors[`${isError}`] && <SPOTNotification text={String(errors[`${isError}`]?.message)} active={true} type={"error"} />}
                        </object>
                    )}
            </div>

            {activePage === "verificationPage" && (
                <div className={"reminder-dialog-wrapper"} id={"code-verification-page"}>
                    <Dialog
                        closeIcon={true}
                        isOpen={true}
                        dialogStyle={{
                            maxWidth: "600px",
                            width: "85%",
                        }}
                        header={subscribeData.channel + t("REMINDER.VERIFICATION")}
                        triggerText={
                            subscribeData.channel && subscribeData.channel === Channel.EMAIL
                                ? t("REMINDER.VERIFICATION_TRIGGER_TEXT_EMAIL")
                                : t("REMINDER.VERIFICATION_TRIGGER_TEXT_SMS")
                        }>
                        <div className={"reminder-spot-component-wrapper"}>
                            <SPOTInput
                                id={"code-verification-input"}
                                name={"code-verification-input"}
                                defaultValue={""}
                                register={register}
                                onChange={onChangeHandler}
                                placeholder={t("REMINDER.PLACEHOLDER_VERIFY_CODE")}
                            />{" "}
                            <br />
                            {isError && isError === "contact-verification-error" && (
                                <SPOTNotification text={t("REMINDER.ERRORS.VERIFY_CONTACT_INFO_ERROR")} active={true} type={"error"} />
                            )}
                            <SPOTButton onClick={isContactChannelVerified} label={t("REMINDER.VERIFY_CODE_BTN")} id={"code-Verification-btn"} type={"primary"} />
                        </div>
                    </Dialog>
                </div>
            )}

            {activePage === "successPage" && (
                <div className={"reminder-dialog-wrapper"} id={"code-verification-success-page"}>
                    <Dialog
                        isOpen={true}
                        closeIcon={true}
                        dialogStyle={{
                            maxWidth: "600px",
                            width: "85%",
                        }}
                        header={subscribeData.channel + t("REMINDER.VERIFICATION")}
                        triggerText={t("REMINDER.VERIFICATION_SUCCESS_TRIGGER_TEXT")}>
                        {t("REMINDER.VERIFICATION_SUCCESS")}
                    </Dialog>
                </div>
            )}

            {isError && isError === "debt-number-error" && errors && errors[`${isError}`] && (
                <object id={`${isError}`} name={`${isError}`}>
                    {<SPOTNotification text={String(errors[`${isError}`]?.message)} active={true} type={"error"} />}
                </object>
            )}
        </div>
    );
};
